import { ReactNode, Component, PureComponent } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { isDefined, isFunction, IsNullOrEmpty } from '@Utils/Micro';
import { Markdown } from '@Primitive/Markdown/MarkdownLazy';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { WebThing, WebThingPort } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { LocalizationMap } from '@Primitive/Localization/Localization';
// =======================================================================================================
enum VerticalAlignment {
    Top = "flex-start",
    Center = "center",
    Bottom = "flex-end"
}
interface MarkdownData {
    Padding: number;
    VerticalAlignment: VerticalAlignment;
    DefaultValue: string;
    EditMode: boolean;
}
interface MdWidgetState {
    source: string;
}
// ------------------------------------------------------------------------------------------------------
class MdWidget extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<MarkdownData>, MdWidgetState> {
    // ---------------------------------------------------------------------------------------------------
    private _prevPortTS: number;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<MarkdownData>) {
        super(props);
        this.state = { source: null };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        this._updateData(this.props);
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillReceiveProps(nextProps: WidgetsThingsStructs.PartialIWidgetThingsProps<MarkdownData>) {
        if (!_.isEqual(this.props.WidgetData, nextProps.WidgetData))
            this._prevPortTS = null;
        this._updateData(nextProps);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const { Padding, VerticalAlignment, EditMode } = this.props.WidgetData;
        return (
            <div
                style={{
                    userSelect: 'text',
                    msUserSelect: 'text',
                    MozUserSelect: 'text',
                    WebkitUserSelect: 'text',
                    display: EditMode ? 'block' : 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: VerticalAlignment
                }}>
                <Markdown
                    width={this.props.width - 17}
                    height={this.props.height}
                    padding={Padding}
                    key={"markdown"}
                    source={this.state.source}
                    editMode={EditMode}
                    onSaveClick={md => this.props.SendPortEvent(PortKeys.onSaveClick, md)} />
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    private _updateData(props: WidgetsThingsStructs.PartialIWidgetThingsProps<MarkdownData>) {

        //try get `PortsStates`
        if (!isDefined(props.PortsStates))
            return;

        // try get port state
        let port = props.PortsStates[PortKeys.Data];
        if (!isDefined(port))
            return;

        // skip if port state was previously processed; check timestamp
        let nextTS = port.Timestamp;
        if (nextTS <= this._prevPortTS)
            return;

        // try get source from port state
        let source = null;
        if (isDefined(port.State)) {
            source = port.State;
        }
        else {
            // 1rst, fallback to default values
            if (!IsNullOrEmpty(props.WidgetData.DefaultValue)) {
                source = props.WidgetData.DefaultValue;
            }
            // 2nd, fallbakc to settings
            else if (_.has(props.WidgetDefaultValues, PortKeys.Data)) {
                source = props.WidgetDefaultValues[PortKeys.Data];
            }
        }

        // update timestamp cache
        if (!isDefined(nextTS))
            nextTS = 0;
        this._prevPortTS = nextTS;
        this.setState({ source: source });
    }
}
// ------------------------------------------------------------------------------------------------------
class MarkdownWidgetSettings extends PureComponent<IWidgetSettingsProps<MarkdownData>> {
    // ------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<MarkdownData>) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.Slider,
                        label: 'Padding',
                        tooltip: 'Sets the space around the markdown content in pixels',
                        dataPath: 'Padding',
                        maxValue: 20,
                        minValue: 0,
                        stepValue: 1,
                        valuePosfix: "px",
                        isHidden: (data: MarkdownData) => data.EditMode
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Vertical Alignment',
                        tooltip: 'Specify the vertical alignment',
                        dataPath: 'VerticalAlignment',
                        items: [
                            { label: "Top", value: VerticalAlignment.Top },
                            { label: "Center", value: VerticalAlignment.Center },
                            { label: "Bottom", value: VerticalAlignment.Bottom },
                        ],
                        isHidden: (data: MarkdownData) => data.EditMode
                    },
                    {
                        label: "Default Value",
                        type: SettingsType.MarkdownString,
                        dataPath: "DefaultValue",
                        tooltip: 'Sets the default value',
                        icon: <i className="fas fa-pen" style={{ fontSize: 16 }} />,
                    },
                    {
                        label: 'Edit Mode',
                        type: SettingsType.Boolean,
                        dataPath: 'EditMode'
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isFunction(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =======================================================================================================
const enum PortKeys {
    Data = "data",
    onSaveClick = 'onsaveclick'
};
export class MarkdownWidget extends IWidgetThingsClass<MarkdownData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <MdWidget />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactNode {
        const parent = this.Parent;
        return <MarkdownWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Add the ports
        let ports: WebThingPort[] = [
            new WebThingPort({
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                State: null
            }, WebThingPortType.Data),
            new WebThingPort({
                Name: 'Save',
                PortKey: PortKeys.onSaveClick,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                State: null
            }, WebThingPortType.Data)
        ];

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): MarkdownData {
        return {
            Padding: 20,
            VerticalAlignment: VerticalAlignment.Top,
            DefaultValue: "",
            EditMode: false
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return MarkdownWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: MarkdownData = MarkdownWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Markdown",
            WidgetTitle: "",
            Key: "MarkdownWidget",
            Type: "MarkdownWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/markdown.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultValues(): { [key: string]: any } {
        return { [PortKeys.Data]: "" };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Data; };
    // ------------------------------------------------------------------------------------------------
}